// Inline entity-detail panel rendered inside the InvestigationSidebar.
//
// Overview, relationships, sources and history share one inspector.
// History stays in its own tab so it cannot push the current properties off screen.

import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink, FileText, Loader2, Merge, Network, Plus, Smartphone } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { conversationReaderLink, excerptReaderLink } from '@/components/comms/comms-links'
import { useDocumentOverlayOptional } from '@/components/document-overlay/DocumentDetailOverlayContext'
import { EntityDetailHeader } from '@/components/investigations/entity-detail/EntityDetailHeader'
import { MergedEntityNotice } from '@/components/investigations/entity-detail/MergedEntityNotice'
import { OpenIdentityLink } from '@/components/investigations/entity-detail/OpenIdentityLink'
import { PassageDetailSection } from '@/components/investigations/entity-detail/PassageDetailSection'
import { isPassage, passageDetail } from '@/components/investigations/entity-detail/passage-detail'
import type { SelectedNode } from '@/components/neo4j/cytoscape/types'
import { NeighborsTab } from '@/components/neo4j/shared/entity-details/NeighborsTab'
import { PropertiesTab } from '@/components/neo4j/shared/entity-details/PropertiesTab'
import { RelationshipsTab } from '@/components/neo4j/shared/entity-details/RelationshipsTab'
import type {
  NeighborEdge,
  NeighborNode,
  RelationshipSummary,
} from '@/components/neo4j/shared/entity-details/types'
import { entityDetailsQueryKey, useEntityDetails } from '@/components/neo4j/shared/useEntityDetails'
import { useEntityNeighbors } from '@/components/neo4j/shared/useEntityNeighbors'
import { Button } from '@/components/ui/button'
import { useApplyContributionToGraph } from '@/features/agent/tools/useApplyContributionToGraph'
import { EntityFacts } from '@/features/analyst-facts/EntityFacts'
import { NoLongerValidToggle } from '@/features/analyst-facts/NoLongerValidToggle'
import { useEntityFactVisibility } from '@/features/analyst-facts/useEntityFactVisibility'
import { EntityHistory } from '@/features/entity-history/EntityHistory'
import { EntityHistoryPreview } from '@/features/entity-history/EntityHistoryPreview'
import { entityUid } from '@/features/entity-history/history-rows'
import { WatchToggle } from '@/features/monitor/WatchToggle'
import { snippetSelection } from '@/features/source-snippets/object-set'
import { SourceSnippetDetail } from '@/features/source-snippets/SourceSnippetDetail'
import { AudioEvidence } from '@/features/voices/AudioEvidence'
import { caseKeys } from '@/hooks/use-case-queries'
import { useInvestigationsStoreApi } from '@/stores/investigations-store-provider'
import type { GraphView } from '@/types/graph-view'
import { cn } from '@/utils/cn'
import { getHydratedNodeLabel } from '@/utils/entity-display'
import { EntityDetailTabs } from './EntityDetailTabs'

// Shape published by the backend's entity-detail endpoint for each source
// document the entity was extracted from. Mirrors `SourceDocumentEntry` in
// `entity-details/SourceDocumentsTab.tsx`.
interface SourceDocumentRef {
  id?: string
  document_id?: string
  name?: string
  path?: string
}

interface SidebarEntityDetailProps {
  caseId: string
  nodeId: string
  presentation?: {
    /** Show the entity header in a standalone sheet. */
    showHeader?: boolean
    /** Show graph actions in research contexts. */
    graphActions?: boolean
    /** The graph row already carries this link beside the name. */
    identityLink?: boolean
  }
}

export function SidebarEntityDetail(props: SidebarEntityDetailProps) {
  const snippet = snippetSelection(props.nodeId)
  return snippet ? (
    <SourceSnippetDetail caseId={props.caseId} id={snippet.id} />
  ) : (
    <GraphSidebarEntityDetail {...props} />
  )
}

function GraphSidebarEntityDetail({ caseId, nodeId, presentation }: SidebarEntityDetailProps) {
  const { showHeader = false, graphActions = false, identityLink = true } = presentation ?? {}
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const applyToGraph = useApplyContributionToGraph(caseId)
  const navigate = useNavigate()
  const overlay = useDocumentOverlayOptional()

  // Same data the popover uses (`api.graph.getNode`) — its top-level
  // `source_documents` field is what the SourceDocumentsRow needs. The probe
  // below carries only the `id` the hook reads.
  // Entity resolution deletes the nodes it folds away, but a saved
  // investigation keeps its own snapshot — so a merged-away entity is still on
  // the canvas and still clickable. When the fetch reports one, the analyst can
  // step through to the survivor and the inspector re-fetches for that id.
  const [mergedRedirectId, setMergedRedirectId] = useState<string | null>(null)
  useEffect(() => setMergedRedirectId(null), [nodeId])
  const activeNodeId = mergedRedirectId ?? nodeId

  const probe = useMemo<SelectedNode>(
    () => ({ id: activeNodeId, name: '', labels: [], properties: {} }),
    [activeNodeId]
  )
  const { fullNodeDetails, isLoading, error, mergedInto } = useEntityDetails({
    caseId,
    selectedNode: probe,
    selectedEdge: null,
  })

  const sourceDocs = useMemo<SourceDocumentRef[]>(() => {
    const raw = (fullNodeDetails as { source_documents?: unknown } | null)?.source_documents
    const list = Array.isArray(raw) ? (raw as SourceDocumentRef[]) : []
    // Backend can return multiple Document nodes for the same logical
    // document (re-extraction artifacts, merge residue): one with
    // name/path filled, another stub. Dedupe by `document_id` and keep
    // the entry that has a name or path so we don't render a phantom
    // "Unknown document" row alongside the real one.
    const byDocId = new Map<string, SourceDocumentRef>()
    const passthrough: SourceDocumentRef[] = []
    for (const doc of list) {
      const key = doc.document_id
      if (!key) {
        // No document_id — can't dedupe; keep as-is (rare but possible).
        passthrough.push(doc)
        continue
      }
      const existing = byDocId.get(key)
      if (!existing) {
        byDocId.set(key, doc)
        continue
      }
      const existingScore = (existing.name ? 1 : 0) + (existing.path ? 1 : 0)
      const candidateScore = (doc.name ? 1 : 0) + (doc.path ? 1 : 0)
      if (candidateScore > existingScore) {
        byDocId.set(key, doc)
      }
    }
    return [...byDocId.values(), ...passthrough]
  }, [fullNodeDetails])

  const selectedNode = useMemo<SelectedNode | null>(() => {
    if (!fullNodeDetails) return null
    return {
      id: fullNodeDetails.id,
      name: fullNodeDetails.name ?? '',
      labels: fullNodeDetails.labels ?? [],
      typeLabel: fullNodeDetails.type ?? fullNodeDetails.labels?.[0],
      properties: fullNodeDetails.properties ?? {},
    }
  }, [fullNodeDetails])

  // A transcript passage is read here, from its own properties, and heard in
  // its recording: its section is the strip of transcript and the way there.
  // Whose each voice is comes with the read, beside the properties (`speakers`).
  // Read once per node, not on every render of the panel (a tab, a neighbours read).
  const passage = useMemo(
    () =>
      selectedNode && isPassage(selectedNode.labels)
        ? passageDetail(caseId, selectedNode.properties, fullNodeDetails?.speakers)
        : null,
    [caseId, selectedNode, fullNodeDetails]
  )

  const neighbors = useEntityNeighbors({ caseId, nodeId: activeNodeId })
  const relationships = useMemo<RelationshipSummary[]>(() => {
    if (!neighbors.data || !selectedNode) return []
    return deriveRelationshipSummaries(selectedNode, neighbors.data)
  }, [neighbors.data, selectedNode])
  // What no longer holds or is wrong stays out unless the switch shows it (PRD-0075 R-C4).
  const facts = useEntityFactVisibility(selectedNode?.properties, relationships)

  const storeApi = useInvestigationsStoreApi()

  const onPropertyUpdate = (updatedNodeId: string) => {
    void queryClient.invalidateQueries({
      queryKey: entityDetailsQueryKey(caseId, updatedNodeId),
    })
    void queryClient.invalidateQueries({
      queryKey: caseKeys.graphEntityDetail(caseId, updatedNodeId),
    })
  }

  const onAddNeighborsToGraph = (added: NeighborNode[], addedEdges: NeighborEdge[]) => {
    if (added.length === 0 && addedEdges.length === 0) return
    const view: GraphView = {
      meta: {
        source: 'chips',
        createdAt: new Date().toISOString(),
        caseId,
        label: t('investigations.entityDetail.neighboursOf', {
          name: selectedNode?.name || activeNodeId,
        }),
      },
      nodes: added.map(n => ({
        id: n.id,
        labels: n.labels,
        typeLabel: n.type ?? n.labels?.[0],
        // Honour `_display_name` and the documented fallback chain so the
        // chip doesn't default to "Default" when the API leaves `n.name`
        // empty.
        name: getHydratedNodeLabel(n),
        properties: n.properties ?? {},
      })),
      edges: addedEdges.map(e => ({
        id: e.id,
        type: e.type,
        source: e.source,
        target: e.target,
        properties: e.properties ?? {},
      })),
      view: {
        hiddenIds: [],
        hiddenTypes: [],
        hiddenRelationshipTypes: [],
        positions: {},
        notes: [],
      },
    }
    const result = storeApi
      .getState()
      .addToObjectSet(view, view.meta.label ?? 'Neighbours', 'expand')
    if (result.addedNodeIds.length > 0 || result.addedEdgeIds.length > 0) {
      toast.success(
        t('investigations.entityDetail.neighboursAdded', {
          count: result.addedNodeIds.length,
        })
      )
    } else {
      toast.message(t('investigations.entityDetail.neighboursAlreadyAdded'))
    }
  }

  // Take the entity (with its 1-hop neighbourhood) over to the graph view —
  // "show" opens it as a fresh exploration, "add" merges it into the active
  // object set. Same contribution plumbing the agent finding cards use.
  const goToGraph = (mode: 'show' | 'add') => {
    if (!selectedNode) return
    const nbrNodes = neighbors.data?.nodes ?? []
    const nbrRels = neighbors.data?.relationships ?? []
    const label = selectedNode.name || activeNodeId
    const view: GraphView = {
      meta: { source: 'chips', createdAt: new Date().toISOString(), caseId, label },
      nodes: [
        {
          id: selectedNode.id,
          labels: selectedNode.labels,
          typeLabel: selectedNode.typeLabel,
          name: selectedNode.name,
          properties: selectedNode.properties,
        },
        ...nbrNodes.map(n => ({
          id: n.id,
          labels: n.labels,
          typeLabel: n.type ?? n.labels?.[0],
          name: getHydratedNodeLabel(n),
          properties: n.properties ?? {},
        })),
      ],
      edges: nbrRels.map(e => ({
        id: e.id,
        type: e.type,
        source: e.source,
        target: e.target,
        properties: e.properties ?? {},
      })),
      view: {
        hiddenIds: [],
        hiddenTypes: [],
        hiddenRelationshipTypes: [],
        positions: {},
        notes: [],
      },
    }
    applyToGraph(mode === 'show' ? 'explore' : 'add', view, label)
  }

  if (isLoading && !fullNodeDetails) {
    return (
      <div className="flex items-center justify-center py-6 text-muted-foreground">
        <Loader2 className="mr-2 size-3.5 animate-spin" />
        <span className="text-2xs">{t('investigations.entityDetail.loading')}</span>
      </div>
    )
  }

  if (mergedInto) {
    return (
      <MergedEntityNotice
        redirect={mergedInto}
        onOpenCanonical={() => setMergedRedirectId(mergedInto.canonicalNodeId)}
      />
    )
  }

  if (error || !selectedNode) {
    return (
      <div className="px-3 py-4 text-2xs text-destructive">
        {t('investigations.entityDetail.loadError')}
      </div>
    )
  }

  // AI-generated one-line summary the extraction pipeline stamps on every
  // entity (`_entity_summary`). It's a private (`_`-prefixed) property so the
  // PropertiesTab hides it — surface it here as a description instead.
  // Where the evidence actually is. A promoted excerpt or a conversation is a
  // node here, but it can only be *read* in the Mobile Forensics reader — the
  // link lands there on that thread with the excerpt in focus (comms-links).
  const isExcerpt = selectedNode.labels.includes('MessageExcerpt')
  const evidenceLink = isExcerpt
    ? excerptReaderLink(caseId, selectedNode.properties)
    : selectedNode.labels.includes('Conversation')
      ? conversationReaderLink(caseId, selectedNode.properties)
      : null

  const summaryRaw = selectedNode.properties._entity_summary
  const summary = typeof summaryRaw === 'string' ? summaryRaw.trim() : ''

  // Real display name (not the raw element id) + primary type for the shared
  // header component.
  const headerLabel =
    selectedNode.typeLabel ?? selectedNode.labels.find(l => !l.startsWith('__') && l !== 'Node')
  const headerName = getHydratedNodeLabel(selectedNode) || activeNodeId

  return (
    <div className="flex flex-col gap-2 border-t border-border/40 px-3 py-3">
      {mergedRedirectId ? (
        <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <Merge className="size-3 shrink-0" />
          {t('investigations.entityDetail.merged.followed')}
        </p>
      ) : null}

      {showHeader ? <EntityDetailHeader name={headerName} typeLabel={headerLabel} /> : null}

      {(identityLink || mergedRedirectId) && selectedNode.labels.includes('Person') && (
        <OpenIdentityLink caseId={caseId} person={{ id: activeNodeId, name: headerName }} />
      )}

      {/* Sledovať (PRD-0075 R-B1): nothing for a node without a stable uid. */}
      <WatchToggle
        caseId={caseId}
        entity={{
          uid: entityUid(selectedNode.properties),
          label: headerName,
          entityType: headerLabel ?? null,
        }}
      />

      {graphActions ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => goToGraph('show')}
          >
            <Network className="size-3.5" />
            {t('investigations.entityDetail.showOnGraph')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => goToGraph('add')}
          >
            <Plus className="size-3.5" />
            {t('investigations.entityDetail.addToGraph')}
          </Button>
        </div>
      ) : null}

      {evidenceLink ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-3xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('comms.evidence.label')}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => navigate(evidenceLink)}
          >
            <Smartphone className="size-3.5" />
            {isExcerpt ? t('comms.evidence.openInReader') : t('comms.evidence.openConversation')}
          </Button>
        </div>
      ) : null}

      {passage ? (
        <PassageDetailSection passage={passage} onOpenDocument={overlay?.open ?? null} />
      ) : null}

      <AudioEvidence
        caseId={caseId}
        labels={selectedNode.labels}
        properties={selectedNode.properties}
        onOpenDocument={overlay?.open ?? null}
      />

      {summary && <p className="text-xs leading-relaxed text-muted-foreground">{summary}</p>}

      <EntityDetailTabs
        key={activeNodeId}
        overview={onOpenHistory => (
          <>
            <EntityHistoryPreview
              caseId={caseId}
              uid={entityUid(selectedNode.properties)}
              onOpenHistory={onOpenHistory}
            />
            <EntityFacts
              caseId={caseId}
              properties={selectedNode.properties}
              typeLabel={headerLabel ?? null}
              documents={sourceDocs.flatMap(doc =>
                doc.document_id ? [{ id: doc.document_id, name: doc.name || doc.document_id }] : []
              )}
            />
            {facts.hasHidden && <NoLongerValidToggle />}
            <PropertiesTab
              caseId={caseId}
              selectedNode={selectedNode}
              selectedEdge={null}
              fullNodeDetails={{ properties: facts.properties, labels: selectedNode.labels }}
              isLoadingNodeDetails={isLoading}
              onPropertyUpdate={onPropertyUpdate}
            />
          </>
        )}
        relationships={
          <>
            {facts.hasHidden && <NoLongerValidToggle />}
            <RelationshipsTab
              selectedNodeId={selectedNode.id}
              relationships={facts.relationships}
              fadedIds={facts.fadedRelationshipIds}
            />
            <div className="space-y-2 border-t border-border/50 pt-3">
              <h3 className="text-xs font-medium">
                {t('investigations.entityDetail.tabs.neighbours')}
              </h3>
              <NeighborsTab
                caseId={caseId}
                nodeId={selectedNode.id}
                onAddToGraph={onAddNeighborsToGraph}
              />
            </div>
          </>
        }
        sources={
          sourceDocs.length > 0 ? (
            <SourceDocumentsRow docs={sourceDocs} onOpen={overlay?.open ?? null} />
          ) : (
            <p className="text-xs text-muted-foreground">
              {t('investigations.entityDetail.noSources')}
            </p>
          )
        }
        history={
          entityUid(selectedNode.properties) ? (
            <EntityHistory
              caseId={caseId}
              properties={selectedNode.properties}
              onOpenDocument={overlay?.open ?? null}
            />
          ) : null
        }
      />
    </div>
  )
}

// Compact list of clickable links to the case-document overlay for every
// source document the entity was extracted from. Falls back to opening the
// raw file path in a new tab when only `path` is known and the overlay
// provider isn't mounted.
function SourceDocumentsRow({
  docs,
  onOpen,
}: {
  docs: SourceDocumentRef[]
  onOpen: ((documentId: string) => void) | null
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1">
      <div className="text-4xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80">
        {t('investigations.entityDetail.sourceDocuments')}
      </div>
      <ul className="flex flex-col gap-0.5">
        {docs.map((doc, idx) => {
          const fileName =
            doc.name ||
            doc.path?.split('/').pop() ||
            t('investigations.entityDetail.unknownDocument')
          const navId = doc.document_id
          const canOpenOverlay = Boolean(navId && onOpen)
          const fallbackUrl = doc.path ? buildFileUrl(doc.path) : null
          const interactive = canOpenOverlay || Boolean(fallbackUrl)
          const handleClick = () => {
            if (canOpenOverlay && navId && onOpen) {
              onOpen(navId)
            } else if (fallbackUrl) {
              window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
            }
          }
          return (
            <li key={doc.id ?? `${fileName}-${idx}`}>
              {interactive ? (
                <button
                  type="button"
                  onClick={handleClick}
                  title={
                    canOpenOverlay
                      ? t('investigations.entityDetail.openDocumentDetail')
                      : t('investigations.entityDetail.openDocumentNewTab')
                  }
                  className={cn(
                    'group flex w-full items-center gap-2 rounded-md px-2 py-2.5 text-left text-2xs text-foreground/85 md:py-1.5',
                    'border border-border/40 bg-card/40 hover:bg-accent/60 hover:text-foreground transition-colors'
                  )}
                >
                  <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{fileName}</span>
                  {canOpenOverlay ? null : (
                    <ExternalLink className="size-3 shrink-0 text-muted-foreground" />
                  )}
                </button>
              ) : (
                <div className="flex items-center gap-2 px-2 py-1.5 text-2xs text-muted-foreground">
                  <FileText className="size-3.5 shrink-0" />
                  <span className="flex-1 truncate">{fileName}</span>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// Only absolute API URLs (/api/v1/files/<s3_key>) are servable; pre-v2
// filesystem paths have no backing mount anymore.
function buildFileUrl(path: string): string | null {
  return path.startsWith('/api/') ? path : null
}

function deriveRelationshipSummaries(
  pivot: SelectedNode,
  data: {
    nodes: { id: string; name: string; labels: string[] }[]
    relationships: {
      id: string
      source: string
      target: string
      type: string
      properties?: Record<string, unknown>
    }[]
  }
): RelationshipSummary[] {
  const byId = new Map<string, { name: string; labels: string[] }>()
  byId.set(pivot.id, { name: pivot.name, labels: pivot.labels })
  for (const n of data.nodes) byId.set(n.id, { name: n.name, labels: n.labels })

  return data.relationships.map(rel => {
    const isOutgoing = rel.source === pivot.id
    const src = byId.get(rel.source)
    const tgt = byId.get(rel.target)
    return {
      id: rel.id,
      type: rel.type,
      sourceId: rel.source,
      sourceName: src?.name ?? rel.source,
      sourceLabels: src?.labels ?? [],
      targetId: rel.target,
      targetName: tgt?.name ?? rel.target,
      targetLabels: tgt?.labels ?? [],
      direction: isOutgoing ? 'outgoing' : 'incoming',
      properties: rel.properties,
    }
  })
}
